import { Radio, ShieldCheck } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

import type { RuntimeMode } from '../types';

interface ModeBannerProps {
    /** `null` while the session is still being read: the pill holds its space but shows nothing. */
    mode: RuntimeMode | null;
    className?: string;
}

/**
 * Mode indicator banner for the trading terminal.
 *
 * - `demo`: "DEMO MODE" with muted styling (non-intrusive).
 * - `real`: "REAL TRADING" with live-state accent styling.
 * - `null`: invisible placeholder of the same size, so the header does not
 *   flash "Demo" and reflow while the session status is still unknown.
 *
 * Designed to be visible but not distracting, fitting the
 * cyberpunk HUD aesthetic of the app.
 */
const ModeBanner: React.FC<ModeBannerProps> = ({ mode, className }) => {
    const isReal = mode === 'real';

    if (mode === null) {
        return (
            <div
                aria-hidden="true"
                className={cn(
                    'invisible inline-flex w-20 items-center rounded-full',
                    className,
                )}
            />
        );
    }

    return (
        <div
            className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 text-xs font-semibold',
                isReal
                    ? 'bg-primary/12 text-primary'
                    : 'bg-info/10 text-info',
                className,
            )}
        >
            {isReal ? (
                <ShieldCheck className="size-3.5" />
            ) : (
                <Radio className="size-3.5" />
            )}
            <span>{isReal ? 'Live' : 'Demo'}</span>
            {isReal && (
                <span aria-hidden="true" className="relative flex size-1.5">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75 motion-reduce:animate-none" />
                    <span className="relative inline-flex size-1.5 rounded-full bg-primary" />
                </span>
            )}
        </div>
    );
};

ModeBanner.displayName = 'ModeBanner';

export default ModeBanner;
